<template>
  <!-- A product replica (StoryReplica) on the feature stage backdrop, the replica twin of
       StoryStage (same .sst styles). With `autoplay`, it plays `states` once when it scrolls into view, then rests
       on the last one with a Replay button (well inside 10 s, WCAG 2.2.2). Without `autoplay`,
       `state` drives it (the sticky chapters). The server and reduce-motion render the last state.
       `label` describes the replica for screen readers (the replica itself is aria-hidden). -->
  <div ref="rootEl" class="sst" :class="{ 'sst--tall': tall, 'sst--cut': cut, 'sst--app': app }" role="img" :aria-label="label">
    <div class="sst-frame">
      <StoryReplica :name="replica" :state="current" />
    </div>
    <button v-show="canReplay" type="button" class="sst-replay" aria-label="Replay preview" title="Replay preview" @click="play">
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" /></svg>
      <span v-if="app" class="sst-replay-label">Replay</span>
    </button>
  </div>
</template>

<script setup>
const props = defineProps({
  replica: { type: String, required: true },
  states: { type: Array, required: true },
  // How long each state holds while playing (ms); the last one rests.
  holds: { type: Object, default: () => ({}) },
  autoplay: { type: Boolean, default: false },
  state: { type: String, default: "" },
  tall: { type: Boolean, default: false },
  // The replica is a whole app screen at the app's own scale: no backdrop, no frame width cap.
  app: { type: Boolean, default: false },
  label: { type: String, default: "" },
});

const final = computed(() => props.states[props.states.length - 1]);
const rootEl = ref(null);
const playing = ref(null);
const resting = ref(false);
// Set for one frame when playback starts, so the jump from the resting frame back to the first
// state is a cut, not a cross-fade of every layer.
const cut = ref(false);
let timers = [];
let observer = null;

const current = computed(() => (props.autoplay ? playing.value ?? final.value : props.state || final.value));
const canReplay = computed(() => props.autoplay && resting.value);

const clear = () => {
  timers.forEach(clearTimeout);
  timers = [];
};
const play = () => {
  clear();
  resting.value = false;
  cut.value = true;
  playing.value = props.states[0];
  requestAnimationFrame(() => requestAnimationFrame(() => { cut.value = false; }));
  let at = 0;
  props.states.forEach((step) => {
    timers.push(setTimeout(() => { playing.value = step; }, at));
    at += props.holds[step] ?? 2000;
  });
  timers.push(setTimeout(() => { resting.value = true; }, at - (props.holds[final.value] ?? 2000) + 600));
};

onMounted(() => {
  if (!props.autoplay) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  observer = new IntersectionObserver(([entry]) => {
    if (!entry.isIntersecting) return;
    observer.disconnect();
    play();
  }, { threshold: 0.35 });
  observer.observe(rootEl.value);
});

onBeforeUnmount(() => {
  clear();
  observer?.disconnect();
});
</script>

<style>
.sst {
  --sst-p1: 124 155 224;
  --sst-p2: 159 139 230;
  --sst-dot: rgb(0 0 0 / 0.1);
  position: relative;
  height: 470px;
  border-radius: 14px;
  background-color: rgb(0 0 0 / 0.018);
  isolation: isolate;
}
.sst--tall { height: 520px; }
/* StoryStage shares the .sst styles and loads after this file, so the app variant doubles its class. */
.sst.sst--app { height: auto; background: transparent; }
.sst.sst--app::before { display: none; }
.sst.sst--app .sst-frame { height: auto; max-width: none; padding: 0; }
/* App screens: a labelled Replay button under the frame's right edge, in the stage's bottom margin,
   so it never covers the replica and appearing never moves anything. */
.sst.sst--app .sst-replay { top: auto; right: 0; bottom: -40px; left: auto; display: inline-flex; gap: 6px; width: auto; height: 32px; border: 1px solid rgb(0 0 0 / 0.12); border-radius: 6px; padding: 0 10px 0 8px; font: 600 13px/1 inherit; }
.dark .sst.sst--app .sst-replay { border-color: rgb(255 255 255 / 0.16); }
.sst.sst--app .sst-replay svg { width: 16px; height: 16px; }
.sst--cut *, .sst--cut *::before, .sst--cut *::after { transition: none !important; }
.sst::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background:
    radial-gradient(circle, var(--sst-dot) 0.8px, transparent 1.3px) 7px 7px / 14px 14px,
    radial-gradient(150% 150% at 50% 45%, rgb(var(--sst-p1) / 0.16) 0%, rgb(var(--sst-p2) / 0.08) 50%, rgb(var(--sst-p2) / 0) 100%);
  -webkit-mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
  mask-image: radial-gradient(closest-side, #000 70%, rgb(0 0 0 / 0.55) 100%);
}
.dark .sst { --sst-dot: rgb(255 255 255 / 0.13); background-color: rgb(255 255 255 / 0.03); }
.sst-frame { height: 100%; max-width: 520px; margin: 0 auto; padding: 28px 28px 30px; }
/* The feature pages (FeatureDetailPage, workspace-voice) show story frames at 540px, as StoryStage
   does; elsewhere they stay 520. Set by class, not by which stylesheet loads last. */
.sst-wide .sst-frame { max-width: 540px; }
.sst-replay {
  position: absolute;
  right: 8px;
  bottom: 4px;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #595f68;
  cursor: pointer;
}
.dark .sst-replay { color: #a3a3a3; }
.sst-replay:hover { background: rgb(0 0 0 / 0.08); }
.dark .sst-replay:hover { background: rgb(255 255 255 / 0.08); }
.sst-replay:focus-visible { outline: 2px solid #0e7490; outline-offset: 1px; }
.sst-replay svg { width: 20px; height: 20px; fill: currentColor; }
@media (max-width: 639px) {
  .sst:not(.sst--app), .sst--tall:not(.sst--app) { height: 500px; }
  .sst-frame { padding: 14px 12px 44px; }
}
</style>
